import { BarChart3, Building2, Cpu, Key, KeyRound, MessageSquarePlus, Shield, ShieldCheck, Users } from "lucide-react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { AdminAuthorizations } from "@/components/admin/AdminAuthorizations";
import { AdminCompanyProfile } from "@/components/admin/AdminCompanyProfile";
import { AdminFeedback } from "@/components/admin/AdminFeedback";
import { AdminGovernance } from "@/components/admin/AdminGovernance";
import { AdminLicense } from "@/components/admin/AdminLicense";
import { AdminModels } from "@/components/admin/AdminModels";
import { AdminOAuthClients } from "@/components/admin/AdminOAuthClients";
import { AdminUsage } from "@/components/admin/AdminUsage";
import { AdminUsers } from "@/components/admin/AdminUsers";
import { PageHeader } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";

const baseAdminNavigation = [
  { name: "Users", href: "/admin/users", icon: Users },
  { name: "Models", href: "/admin/models", icon: Cpu },
  { name: "Usage", href: "/admin/usage", icon: BarChart3 },
  { name: "Governance", href: "/admin/governance", icon: ShieldCheck },
  { name: "Feedback", href: "/admin/feedback", icon: MessageSquarePlus },
  { name: "Company", href: "/admin/company", icon: Building2 },
  { name: "Authorizations", href: "/admin/authorizations", icon: KeyRound },
  { name: "License", href: "/admin/license", icon: Key },
];

const oauthClientsNavItem = { name: "OAuth Clients", href: "/admin/oauth-clients", icon: KeyRound };

export function Admin() {
  const { user, profile } = useAuth();
  const oauthClientManagement = profile?.capabilities?.oauthClientManagementEnabled ?? false;
  const adminNavigation = oauthClientManagement ? [...baseAdminNavigation, oauthClientsNavItem] : baseAdminNavigation;

  if (!user?.isPlatformAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="h-16 w-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-content-primary">Access Denied</h2>
          <p className="mt-2 text-content-tertiary max-w-sm">
            You don't have permission to access the admin area. Contact your administrator for access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <PageHeader icon={Shield} title="Admin" subtitle="Manage users, models, and platform settings">
        {/* Tabs */}
        <div className="mt-6 flex gap-1 bg-surface-primary p-1 rounded-lg w-fit">
          {adminNavigation.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-surface-secondary text-content-primary"
                      : "text-content-tertiary hover:text-content-primary"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {item.name}
              </NavLink>
            );
          })}
        </div>
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <Routes>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="models" element={<AdminModels />} />
          <Route path="usage" element={<AdminUsage />} />
          <Route path="governance" element={<AdminGovernance />} />
          <Route path="feedback" element={<AdminFeedback />} />
          <Route path="company" element={<AdminCompanyProfile />} />
          <Route path="authorizations" element={<AdminAuthorizations />} />
          <Route path="license" element={<AdminLicense />} />
          {oauthClientManagement && <Route path="oauth-clients" element={<AdminOAuthClients />} />}
        </Routes>
      </div>
    </div>
  );
}
